import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Pause, MusicNotes } from "@phosphor-icons/react";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

export default function AudioPlayer({ src, accent = "hsl(351 100% 78%)", onPlay, testid, maxDuration }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [errored, setErrored] = useState(false);

  // Duración efectiva: limitada a maxDuration (preview corto) si aplica
  const effDur = maxDuration && duration ? Math.min(duration, maxDuration) : duration;

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      // Cortar la reproducción al llegar al límite del preview
      if (maxDuration && a.currentTime >= maxDuration) {
        a.pause();
        a.currentTime = 0;
        setPlaying(false);
        setCurrent(0);
        setProgress(0);
        return;
      }
      const dur = maxDuration ? Math.min(a.duration || 0, maxDuration) : a.duration;
      setCurrent(a.currentTime);
      setProgress(dur ? (a.currentTime / dur) * 100 : 0);
    };
    const onMeta = () => setDuration(a.duration);
    const onEnd = () => { setPlaying(false); setProgress(0); setCurrent(0); };
    const onErr = () => { setErrored(true); setPlaying(false); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    a.addEventListener("error", onErr);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("error", onErr);
    };
  }, [maxDuration]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a || errored) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      onPlay?.(a);
      const p = a.play();
      if (p && p.catch) p.catch(() => setErrored(true));
      setPlaying(true);
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const dur = maxDuration ? Math.min(a.duration, maxDuration) : a.duration;
    a.currentTime = pct * dur;
  };

  return (
    <div className="flex items-center gap-4" data-testid={testid}>
      <audio ref={audioRef} src={src} preload="metadata" />
      <motion.button
        type="button"
        onClick={toggle}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        data-testid={testid ? `${testid}-toggle` : undefined}
        className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-soft"
        style={{ background: accent }}
        aria-label={playing ? "Pausar" : "Reproducir"}
      >
        {playing ? <Pause weight="fill" size={24} /> : <Play weight="fill" size={24} className="ml-0.5" />}
      </motion.button>
      <div className="flex-1 min-w-0">
        <div onClick={errored ? undefined : seek} className="h-2.5 rounded-full bg-muted cursor-pointer overflow-hidden">
          <div className="h-full rounded-full transition-[width] duration-150" style={{ width: `${progress}%`, background: accent }} />
        </div>
        <div className="flex items-center justify-between mt-2 text-xs font-bold text-foreground/40">
          {errored ? (
            <span className="text-foreground/40">Audio no disponible</span>
          ) : (
            <>
              <span className="flex items-center gap-1"><MusicNotes weight="duotone" size={14} /> {fmt(current)}</span>
              <span>{fmt(effDur)}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
